import { Observable, map } from "rxjs";
import { computed, effect, inject, Injectable, signal } from "@angular/core";

import { HttpService } from "../http/http.service";
import { ApplicationUserTypeName, DiscountType, UserRole } from "../../enums";

import { environment } from "src/environments/environment";
import { UsersService } from "src/app/features";
import { Permissions } from "src/assets/config/config";

interface LoginResponse {
  token: string;
  tokenValidTo: string;
  userId: string;
  nameEn: string | null;
  nameAr: string | null;
}

@Injectable({
  providedIn: "root",
})
export class AuthService extends HttpService {
  private usersService = inject(UsersService);
  protected override get baseUrl(): string {
    return "accounts/";
  }

  readonly user = signal(this.convertTokenJWT());

  readonly userId = computed(() => this.user()?.UserId);
  readonly userRole = computed(() => this.user()?.RoleCode);
  readonly userType = computed(() => this.user()?.UserType);

  readonly isAdmin = computed(() => this.userRole() === UserRole.ADMIN);
  readonly isGuest = computed(() => this.userRole() === UserRole.GUEST);
  readonly isKiosk = computed(() => this.userRole() === UserRole.KIOSK);
  readonly isCounter = computed(() => this.userRole() === UserRole.COUNTER);
  readonly isVisitorService = computed(() => this.userRole() === UserRole.VISITORSERVICE);
  readonly isHasAdminRole = computed(() => Permissions.Admin.includes(this.userRole()!));

  readonly isCompanyUser = computed(() => {
    const user = this.user();
    if (!user) return false;

    const isUserTypeCompany = this.userType() === ApplicationUserTypeName.Company;
    const isHasCompanyProperties = Object.hasOwn(user, "DiscountType") && Object.hasOwn(user, "DiscountValue");
    return isUserTypeCompany && isHasCompanyProperties;
  });

  readonly isCompanyMembershipUser = computed(() => {
    const user = this.user();
    if (!user) return false;

    const isHasMembershipProperties =
      Object.hasOwn(user, "MembershipId") && Object.hasOwn(user, "IsActiveMembership") && user.IsActiveMembership?.toLowerCase() === "true";
    const isUserTypeCompanyMembership = this.userType() === ApplicationUserTypeName.CompanyMembership;
    return isUserTypeCompanyMembership && isHasMembershipProperties;
  });

  readonly companyMembershipTotalCards = computed(() => {
    let count = this.usersService.currentUser()?.membership?.totalNumberOfCards;
    if (!count || !this.isCompanyMembershipUser()) return 0;

    return +count;
  });

  readonly discountValue = computed(() => (this.isCompanyUser() ? Number(this.user()?.DiscountValue) : 0));
  readonly discountType = computed(() => (this.isCompanyUser() ? Number(this.user()?.DiscountType) : 0));
  readonly isPercentageDiscount = computed(() => this.discountType() === DiscountType.Percentage);
  readonly isFixedAmountDiscount = computed(() => this.discountType() === DiscountType.Amount);

  readonly isOfficialInstitutionUser = computed(() => {
    const user = this.user();
    if (!user) return false;

    const isUserTypeOfficialInstitute = this.userType() === ApplicationUserTypeName.OfficialInstitute;
    const isHasOfficialInstituteProperties =
      Object.hasOwn(user, "EntranceType") && Object.hasOwn(user, "Amount") && Object.hasOwn(user, "HaveOfficialInstitute");

    return isUserTypeOfficialInstitute && isHasOfficialInstituteProperties;
  });

  readonly officialInstitutionAmount = computed(() => (this.isOfficialInstitutionUser() ? Number(this.user()?.Amount) : 0));

  readonly isMembershipUser = computed(() => {
    const user = this.user();
    if (!user) return false;

    const isHasMembershipProperties =
      Object.hasOwn(user, "MembershipId") && Object.hasOwn(user, "IsActiveMembership") && user.IsActiveMembership?.toLowerCase() === "true";
    const isUserTypeMembership = this.userType() === ApplicationUserTypeName.Membership;

    return isHasMembershipProperties && isUserTypeMembership;
  });

  constructor() {
    super();

    effect(() => {
      if (this.userId()) this.usersService.getCurrentUser(this.userId() ?? "").subscribe();
      else this.usersService.currentUser.set(null);
    });
  }

  login(body: unknown): Observable<any> {
    return this.post<LoginResponse>({ APIName: "login", body }).pipe(
      map((res) => {
        localStorage.setItem(environment.tokenPropertyName, res.token);
        localStorage.setItem("userData", JSON.stringify(res));
        this.user.set(this.convertTokenJWT());
        this.usersService.getCurrentUser(this.user()?.UserId ?? "").subscribe();

        this.toastrService.success(this.translation.instant(`BE_VALIDATION.LOGIN_SUCCESS`, { userName: this.user()?.NameEn }));
        return res;
      }),
    );
  }

  logOut() {
    this.user.set(null);
    this.usersService.currentUser.set(null);
    localStorage.clear();
  }

  loginAsGuest() {
    return this.get<LoginResponse>({ APIName: "login-as-guest" }).pipe(
      map((res) => {
        localStorage.setItem(environment.tokenPropertyName, res.token);
        localStorage.setItem("userData", JSON.stringify(res));
        this.user.set(this.convertTokenJWT());
        this.usersService.getCurrentUser(this.user()?.UserId ?? "").subscribe();

        return res;
      }),
    );
  }

  register(body: Record<string, unknown>) {
    return this.post<Record<string, unknown>>({ APIName: "register", body, showAlert: true });
  }

  confirmOtp(body: Record<string, unknown>) {
    return this.post({ APIName: "confirm-otp", body, showAlert: true });
  }

  verifyEmail(body: Record<string, unknown>) {
    return this.post({ APIName: "verify-email", body, showAlert: true });
  }

  resetPassword(body: Record<string, unknown>) {
    return this.post({ APIName: "reset-password", body, showAlert: true });
  }

  completeResetPasswordToken(body: Record<string, unknown>) {
    return this.post({ APIName: "complete-reset-password-token", body, showAlert: true });
  }

  completeResetPasswordOtp(body: Record<string, unknown>) {
    return this.post({ APIName: "complete-reset-password-otp", body, showAlert: true });
  }

  resendMobileOtp(body: Record<string, unknown>) {
    return this.post({ APIName: "resend-mobile-otp", body, showAlert: true });
  }

  // private usersService = inject(UsersService);

  // userPermissions: BehaviorSubject<any[] | any> = new BehaviorSubject(null);

  // get userPermissions$(): Observable<any[]> {
  //   if (this.userPermissions.value) {
  //     return this.userPermissions.asObservable();
  //   }

  //   return this.usersService.getUserPermissions().pipe(map(value => {
  //     this.userPermissions.next(value);
  //     return value;
  //   }));
  // }
}
